import { useLayoutEffect, useRef, useState } from 'react';

/** The pane's pager: one button per page, shown only when there is more than one. */
export function PanePager({ total, size, page, onPage, noun, all, id, countInk = 'class' }: { total: number; size: number; page: number; onPage: (p: number) => void; noun: string; all?: boolean; id?: string; /**
   * How the reference writes the count's muted ink. views/list.ejs writes `<span class="muted">`; the admin
   * users pane writes `<span style="color: var(--muted)">` — the same look, a different class list.
   */
  countInk?: 'class' | 'inline' }) {
  const pages = Math.max(1, Math.ceil(total / size));
  // One page is not a pager, it is a count — and "All" has no pages to step through either, so it shows the
  // count too (the EJS pager's showCount() path). Returning null for "All" left the bar saying "Show All" and
  // nothing else, which is the one state where the reader most wants the row count.
  if (pages <= 1 || all) {
    return (
      <div className="pane-pager t-small" id={id}>
        {/* Muted, as the EJS pager writes its count — the number is the pager's own text, not a heading. */}
        {countInk === 'inline' ? (
          <span style={{ color: 'var(--muted)' }}>{`${total} ${noun}`}</span>
        ) : (
          <span className="muted">{`${total} ${noun}`}</span>
        )}
      </div>
    );
  }
  return (
    <div className="pane-pager t-small" id={id} style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, margin: 0 }}>
      {/* The ends are icons with labels, not text: the pager's own text is the page numbers, and a "‹" glyph in
          the caption line reads as another number. */}
      <button
        type="button"
        className="ghost pane-pager-prev"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onPage(Math.max(1, page - 1))}
        style={{ padding: '1px 6px', borderRadius: 5 }}
      >
        <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M8 2 L4 6 L8 10" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      </button>
      {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          className={n === page ? 'ghost seg-dir' : 'ghost'}
          aria-current={n === page ? 'page' : undefined}
          onClick={() => onPage(n)}
          style={{ padding: '1px 7px', borderRadius: 5 }}
        >
          {n}
        </button>
      ))}
      <button
        type="button"
        className="ghost pane-pager-next"
        aria-label="Next page"
        disabled={page >= pages}
        onClick={() => onPage(Math.min(pages, page + 1))}
        style={{ padding: '1px 6px', borderRadius: 5 }}
      >
        <svg width="9" height="9" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M4 2 L8 6 L4 10" fill="none" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      </button>
    </div>
  );
}

/** The trigger + menu the EJS pane uses (`.pane-pagesize-btn` → `.pf-pagesize-menu`). */
export function PanePageSize({ value, onChange, noun, id }: {
  value: string;
  onChange: (v: string) => void;
  noun: string;
  /** Accepted for the call sites' sake; the control itself says "All", not "All 5 transactions". */
  total?: number;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  // The menu is a FIXED overlay anchored to the control, as list.ejs builds it: `left: r.left`, six pixels
  // under the button, flipped above it when the pane bar sits near the bottom of the window. It used to be an
  // absolutely positioned, unstyled div — so it took the browser's default button paint and laid its choices out
  // in a row instead of a column.
  const btn = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setPlace(null);
      return;
    }
    const where = () => {
      const b = btn.current;
      if (!b) return;
      const r = b.getBoundingClientRect();
      const h = menu.current ? menu.current.offsetHeight : 0;
      // EJS: open upward when the bar is within h + 8px of the window's bottom and there is room above.
      const top = window.innerHeight - r.bottom < h + 8 && r.top > h + 8 ? Math.round(r.top - h - 6) : Math.round(r.bottom + 6);
      setPlace({ left: Math.round(r.left), top });
    };
    where();
    window.addEventListener('resize', where);
    window.addEventListener('scroll', where, true);
    return () => {
      window.removeEventListener('resize', where);
      window.removeEventListener('scroll', where, true);
    };
  }, [open]);

  // The pager's five choices; a page that needs a smaller page adds it (the admin users table shows five).
  const choices = id === 'users-pagesize-btn' ? ['5', '10', '25', '50', '100', 'all'] : ['10', '25', '50', '100', 'all'];
  // views/admin.ejs names the two halves of the control (`users-pagesize-val`, `users-pagesize-suffix`), and the
  // page's own script and CSS reach for those ids; they are derived from the trigger's id the way the page does.
  const pid = id ? id.replace(/-btn$/, '') : '';
  return (
    // No wrapper element: views/admin.ejs puts the trigger straight inside `.pane-pagesize` (which is already
    // the positioned, flex parent), so an extra div here is markup the reference does not have.
    <>
      <button
        type="button"
        ref={btn}
        id={id}
        className="pane-pagesize-btn t-small"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title="Rows per page"
      >
        {/* The control is two spans and a chevron, as the EJS pane builds it: "All" drops the suffix, and the
            suffix is muted. A single text node lost both the weight difference and the chevron. */}
        <span className="pane-pagesize-val" id={pid ? `${pid}-val` : undefined}>
          {value === 'all' ? 'All' : value}
        </span>
        <span
          className="muted pane-pagesize-suffix"
          id={pid ? `${pid}-suffix` : undefined}
          style={value === 'all' ? { display: 'none' } : undefined}
        >
          per page
        </span>
        <svg width={10} height={10} viewBox="0 0 12 12" aria-hidden="true">
          <path d="M2 4 L6 8 L10 4" fill="none" stroke="var(--muted)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open ? (
        <div
          ref={menu}
          className="pf-pagesize-menu"
          role="menu"
          style={{
            position: 'fixed',
            left: place ? place.left : -9999,
            top: place ? place.top : -9999,
            zIndex: 9999,
            minWidth: 144,
            padding: 4,
            background: 'var(--panel)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}
        >
          {choices.map((c) => {
            const on = String(c) === String(value);
            return (
              <button
                key={c}
                type="button"
                role="menuitem"
                className="pf-pagesize-item"
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '5px 10px',
                  fontSize: 12,
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  border: 0,
                  borderRadius: 5,
                  cursor: 'pointer',
                  color: 'var(--text)',
                  background: on ? 'var(--accent-tint-soft)' : 'transparent',
                }}
              >
                {/* The EJS menu names the noun ("All dividends"); the tick is a separate span in the accent. */}
                <span>{c === 'all' ? `All ${noun}` : c}</span>
                {on ? <span style={{ color: 'var(--accent)' }}>✓</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

